import { z } from 'zod';

import { ACCESS_LEVELS, ACCESS_OVERLAYS } from '../domain/enums.js';
import { emailSchema, paginationQuerySchema, passwordSchema, uuidSchema } from './common.js';

export const departmentAccessInputSchema = z.object({
  departmentId: uuidSchema,
  level: z.enum(ACCESS_LEVELS),
});

export type DepartmentAccessInput = z.infer<typeof departmentAccessInputSchema>;

const nameSchema = z.string().trim().min(1).max(80);

export const createUserSchema = z
  .object({
    email: emailSchema,
    firstName: nameSchema,
    lastName: nameSchema,
    password: passwordSchema,
    jobRoleId: uuidSchema,
    homeDepartmentId: uuidSchema,
    overlays: z.array(z.enum(ACCESS_OVERLAYS)).max(3).default([]),
    extraAccess: z.array(departmentAccessInputSchema).max(30).default([]),
    isAssignable: z.boolean().default(true),
  })
  .strict();

export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z
  .object({
    firstName: nameSchema.optional(),
    lastName: nameSchema.optional(),
    jobRoleId: uuidSchema.optional(),
    homeDepartmentId: uuidSchema.optional(),
    overlays: z.array(z.enum(ACCESS_OVERLAYS)).max(3).optional(),
    extraAccess: z.array(departmentAccessInputSchema).max(30).optional(),
    isActive: z.boolean().optional(),
    isAssignable: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const resetUserPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    requireChangeOnNextLogin: z.boolean().default(true),
  })
  .strict();

export type ResetUserPasswordInput = z.infer<typeof resetUserPasswordSchema>;

export const userListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().min(1).max(120).optional(),
  departmentId: uuidSchema.optional(),
  jobRoleId: uuidSchema.optional(),
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true')),
});

export type UserListQuery = z.infer<typeof userListQuerySchema>;

export const assignableQuerySchema = z.object({
  departmentId: uuidSchema,
});
